import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Download,
  FileText,
  Search,
  Plus,
  BookOpen,
  Trash2,
  Eye,
  X,
  ChevronRight,
} from 'lucide-react';
import api from '../utils/api';

interface Resource {
  id: string;
  title: string;
  description?: string;
  filePath: string;
  originalName?: string;
  mimeType?: string;
  fileSize?: number;
  fileExtension?: string;
  resourceType: 'HOMEWORK' | 'LESSON_PLAN';
  createdAt: string;
  subjects: {
    id: string;
    name: string;
    category?: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  }[];
}

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
}

interface LessonResource {
  id: string;
  assignmentType: 'LESSON_MATERIAL' | 'HOMEWORK';
  isHomework: boolean;
  assignedAt: string;
  resource: Resource;
}

interface BookingWithResources {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  subject: {
    id: string;
    name: string;
    category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  };
  tutor: {
    id: string;
    name: string;
  };
  lessonResources: LessonResource[];
}

// ---------------------------------------------------------------------------
// Bucketing logic
// ---------------------------------------------------------------------------

type BucketKey = 'MATHS' | 'ENGLISH' | 'NON_VERBAL' | 'VERBAL' | 'OTHER';

interface BucketDef {
  key: BucketKey;
  label: string; // display label
  eyebrow: string; // eyebrow label (uppercased already)
  matches: (subjectName: string) => boolean;
}

// Order matters: non-verbal is checked before verbal so "Non-verbal Reasoning"
// doesn't get misrouted to the Verbal bucket.
const BUCKETS: BucketDef[] = [
  {
    key: 'MATHS',
    label: 'Maths',
    eyebrow: 'MATHS',
    matches: (name) => /maths|math/i.test(name),
  },
  {
    key: 'ENGLISH',
    label: 'English',
    eyebrow: 'ENGLISH',
    matches: (name) => /english/i.test(name),
  },
  {
    key: 'NON_VERBAL',
    label: 'Non-verbal Reasoning',
    eyebrow: 'NON-VERBAL REASONING',
    matches: (name) => /non[- ]?verbal/i.test(name),
  },
  {
    key: 'VERBAL',
    label: 'Verbal Reasoning',
    // /^verbal/i after non-verbal has already been filtered.
    eyebrow: 'VERBAL REASONING',
    matches: (name) => /^verbal/i.test(name.trim()),
  },
];

function bucketForSubjectName(rawName: string): BucketKey {
  const name = rawName.trim();
  // Non-verbal must be checked before verbal.
  if (BUCKETS[2].matches(name)) return 'NON_VERBAL';
  if (BUCKETS[0].matches(name)) return 'MATHS';
  if (BUCKETS[1].matches(name)) return 'ENGLISH';
  if (BUCKETS[3].matches(name)) return 'VERBAL';
  return 'OTHER';
}

function bucketsForResource(resource: Resource): Set<BucketKey> {
  const keys = new Set<BucketKey>();
  if (!resource.subjects || resource.subjects.length === 0) {
    keys.add('OTHER');
    return keys;
  }
  for (const subj of resource.subjects) {
    keys.add(bucketForSubjectName(subj.name));
  }
  return keys;
}

// Previewable extensions (images + PDF render inline in most browsers).
const PREVIEWABLE_EXT = /\.(pdf|png|jpe?g|webp)$/i;
function isPreviewable(resource: Resource): boolean {
  const name = resource.originalName || resource.filePath || '';
  if (PREVIEWABLE_EXT.test(name)) return true;
  if (resource.fileExtension && PREVIEWABLE_EXT.test('.' + resource.fileExtension.replace(/^\./, ''))) return true;
  if (resource.mimeType) {
    return /^(application\/pdf|image\/(png|jpeg|webp))$/.test(resource.mimeType);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ResourcesPage: React.FC = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [lessonBookings, setLessonBookings] = useState<BookingWithResources[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    subjectIds: [] as string[],
    file: null as File | null,
  });
  const [expandedBucket, setExpandedBucket] = useState<BucketKey | null>(null);
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchResources();
      if (user.role === 'STUDENT') {
        fetchLessonResources();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, user]);

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      if (Array.isArray(response.data)) {
        setSubjects(response.data);
      } else {
        console.warn('Subjects API returned non-array data:', response.data);
        setSubjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setSubjects([]);
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    try {
      let url = '/resources';
      if (user?.role === 'STUDENT') {
        url = '/resources/my-resources';
      }
      const params = new URLSearchParams();
      if (selectedSubject) params.append('subjectId', selectedSubject);

      const response = await api.get(`${url}?${params}`);
      if (Array.isArray(response.data)) {
        setResources(response.data);
      } else {
        console.warn('Resources API returned non-array data:', response.data);
        setResources([]);
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonResources = async () => {
    try {
      if (user?.role === 'STUDENT') {
        const response = await api.get(`/lesson-resources/student/${user.id}`);
        if (Array.isArray(response.data)) {
          setLessonBookings(response.data);
        } else {
          console.warn('Lesson resources API returned non-array data:', response.data);
          setLessonBookings([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch lesson resources:', error);
      setLessonBookings([]);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.title || !uploadData.subjectIds.length) {
      window.alert('Please add a title, select at least one subject, and choose a file');
      return;
    }

    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);
    formData.append('subjectIds', JSON.stringify(uploadData.subjectIds));
    formData.append('file', uploadData.file);

    try {
      await api.post('/resources', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // large uploads can exceed the default timeout
      });
      setShowUploadForm(false);
      setUploadData({ title: '', description: '', subjectIds: [], file: null });
      fetchResources();
      window.alert('Resource uploaded successfully!');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to upload resource');
    }
  };

  const handleDownload = async (resourceId: string) => {
    try {
      const response = await api.get(`/resources/download/${resourceId}`, {
        responseType: 'blob',
        timeout: 300000, // large downloads can exceed the default timeout
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'resource';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"'\n]*?)\1/);
        if (filenameMatch && filenameMatch[2]) {
          filename = filenameMatch[2];
        }
      }

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        if (error.response?.data?.message) {
          window.alert(error.response.data.message);
        } else {
          window.alert('Resource not found. It may have been deleted or moved.');
        }
      } else if (error.response?.status === 403) {
        window.alert('Access denied. This resource is not available to you.');
      } else if (error.response?.status === 401) {
        window.alert('Please log in to download resources.');
      } else {
        window.alert('Failed to download resource. Please try again later.');
      }
    }
  };

  const handleDelete = async (resourceId: string, resourceTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${resourceTitle}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/resources/${resourceId}`);
      fetchResources();
      window.alert('Resource deleted successfully!');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete resource');
    }
  };

  // Preview: fetch the file as a blob (same auth path as download) and display
  // in an iframe modal. Falls back to opening in a new tab when not previewable.
  const openPreview = async (resource: Resource) => {
    if (!isPreviewable(resource)) {
      // Not previewable inline — just trigger the download flow.
      handleDownload(resource.id);
      return;
    }
    try {
      const response = await api.get(`/resources/download/${resource.id}`, {
        responseType: 'blob',
        timeout: 300000, // large downloads can exceed the default timeout
      });
      const contentType = response.headers['content-type'] || resource.mimeType || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
      setPreviewResource(resource);
    } catch (error) {
      console.error('Preview error:', error);
      window.alert('Unable to preview this file. Try downloading it instead.');
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) window.URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewResource(null);
  };

  // ---- derived data ----

  const filteredResources = useMemo(() => {
    const arr = Array.isArray(resources) ? resources : [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return arr;
    return arr.filter(
      (r) =>
        r.title.toLowerCase().includes(term) ||
        (r.description ? r.description.toLowerCase().includes(term) : false)
    );
  }, [resources, searchTerm]);

  const bucketedResources = useMemo(() => {
    const map: Record<BucketKey, Resource[]> = {
      MATHS: [],
      ENGLISH: [],
      NON_VERBAL: [],
      VERBAL: [],
      OTHER: [],
    };
    for (const r of filteredResources) {
      const keys = Array.from(bucketsForResource(r));
      for (const k of keys) {
        map[k].push(r);
      }
    }
    return map;
  }, [filteredResources]);

  // For the "Upload to {bucket}" button, pick subject IDs whose name matches the bucket.
  const subjectIdsForBucket = (bucket: BucketKey): string[] => {
    if (bucket === 'OTHER') return [];
    return subjects
      .filter((s) => bucketForSubjectName(s.name) === bucket)
      .map((s) => s.id);
  };

  const openUploadFormForBucket = (bucket: BucketKey) => {
    const defaultIds = subjectIdsForBucket(bucket);
    setUploadData({
      title: '',
      description: '',
      subjectIds: defaultIds,
      file: null,
    });
    setShowUploadForm(true);
    // Modal opens in place (centred fixed overlay) — no scrolling needed,
    // so we don't scrollIntoView any more.
  };

  // ---- formatting helpers ----

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'Oxbridge';
      default:
        return category;
    }
  };

  const formatSubjectWithLevel = (subject: Subject) =>
    `${subject.name} - ${formatSubjectCategory(subject.category)}`;

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeDisplayName = (mimeType?: string, extension?: string) => {
    if (!mimeType && !extension) return 'File';
    const typeMap: { [key: string]: string } = {
      'application/pdf': 'PDF',
      'text/html': 'HTML',
      'application/msword': 'Word Document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
      'text/plain': 'Text File',
      'text/markdown': 'Markdown',
      'image/jpeg': 'JPEG Image',
      'image/png': 'PNG Image',
      'image/gif': 'GIF Image',
      'image/svg+xml': 'SVG Image',
      'text/css': 'CSS File',
      'application/javascript': 'JavaScript',
      'application/json': 'JSON File',
      'text/csv': 'CSV File',
    };
    if (mimeType && typeMap[mimeType]) return typeMap[mimeType];
    if (extension) return extension.toUpperCase().replace('.', '') + ' File';
    return 'File';
  };

  // ---------------------------------------------------------------------------
  // Tutor view — 4-bucket dynamic grid
  // ---------------------------------------------------------------------------

  /**
   * Dynamic sizing:
   *   span = clamp(3, round(12 * count / totalMax), 12)
   * Where totalMax = max count across all populated non-Other buckets (fallback 1).
   * If a bucket is empty we still render it at span 3 so tutors can upload into it.
   * The "Other" bucket takes the remaining row width (full row if present).
   * On < 768px Tailwind's grid collapses because we gate the grid classes on md:.
   */
  const renderTutorBucketGrid = () => {
    const nonOtherKeys: BucketKey[] = ['MATHS', 'ENGLISH', 'NON_VERBAL', 'VERBAL'];
    const counts = nonOtherKeys.map((k) => bucketedResources[k].length);
    const totalMax = Math.max(1, ...counts);

    const spans: Record<BucketKey, number> = {
      MATHS: 3,
      ENGLISH: 3,
      NON_VERBAL: 3,
      VERBAL: 3,
      OTHER: 12,
    };
    for (const k of nonOtherKeys) {
      const count = bucketedResources[k].length;
      if (count <= 0) {
        spans[k] = 3; // empty bucket: minimum width, still visible.
      } else {
        const raw = Math.round((12 * count) / totalMax);
        spans[k] = Math.max(3, Math.min(12, raw));
      }
    }
    // Ensure the four non-Other tiles fit on the 12-col grid per row by
    // scaling back proportionally if the sum exceeds 12 on a row basis.
    const totalSpan = nonOtherKeys.reduce((s, k) => s + spans[k], 0);
    if (totalSpan > 24) {
      // More than two rows worth — scale so max row works. Keep min 3.
      const scale = 24 / totalSpan;
      for (const k of nonOtherKeys) {
        spans[k] = Math.max(3, Math.min(12, Math.round(spans[k] * scale)));
      }
    }

    const allBuckets: BucketKey[] = [...nonOtherKeys, 'OTHER'];

    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {allBuckets.map((key) => {
          if (key === 'OTHER' && bucketedResources.OTHER.length === 0) return null;
          const def =
            key === 'OTHER'
              ? { key, label: 'Other', eyebrow: 'OTHER' }
              : BUCKETS.find((b) => b.key === key)!;
          const list = bucketedResources[key];
          const isExpanded = expandedBucket === key;
          const visible = isExpanded ? list : list.slice(0, 5);
          return (
            <div
              key={key}
              className="md:col-span-12"
              style={{ gridColumn: `span ${spans[key]} / span ${spans[key]}` }}
            >
              <section className="card h-full flex flex-col">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow">{def.eyebrow}</div>
                    <h2 className="font-serif text-2xl md:text-3xl mt-2 text-ink">{def.label}</h2>
                    <p className="text-sm text-muted mt-1">
                      {list.length} {list.length === 1 ? 'resource' : 'resources'}
                    </p>
                  </div>
                  <button
                    onClick={() => openUploadFormForBucket(key)}
                    className="btn btn-secondary text-sm px-3 py-2"
                    title={`Upload to ${def.label}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Upload
                  </button>
                </div>

                <div className="mt-4 border-t border-rule pt-4 flex-1">
                  {list.length === 0 ? (
                    <p className="text-sm text-muted italic">No resources yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {visible.map((r) => {
                        const Icon = FileText;
                        return (
                          <li
                            key={r.id}
                            className="group flex items-start justify-between gap-3 py-2 border-b border-rule last:border-b-0"
                          >
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <Icon className="w-4 h-4 text-ochre mt-1 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-ink text-sm font-medium truncate">
                                  {r.title}
                                </div>
                                <div className="text-xs text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                                  <span>{getFileTypeDisplayName(r.mimeType, r.fileExtension)}</span>
                                  {r.fileSize ? (
                                    <>
                                      <span aria-hidden="true">·</span>
                                      <span>{formatFileSize(r.fileSize)}</span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => openPreview(r)}
                                className="p-1.5 text-muted hover:text-ochre transition-colors"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownload(r.id)}
                                className="p-1.5 text-muted hover:text-ochre transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(r.id, r.title)}
                                className="p-1.5 text-muted hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {list.length > 5 && (
                    <button
                      onClick={() => setExpandedBucket(isExpanded ? null : key)}
                      className="mt-3 text-sm text-ochre hover:underline inline-flex items-center gap-1"
                    >
                      {isExpanded ? 'Show less' : `+${list.length - 5} more`}
                      <ChevronRight
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </button>
                  )}
                </div>
              </section>
            </div>
          );
        })}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="container-content py-8 md:py-12">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="eyebrow">Library</div>
          <h1 className="font-serif text-display text-ink mt-2">Resources</h1>
          <p className="text-muted mt-2 max-w-xl">
            {user?.role === 'TUTOR'
              ? 'Study materials organised by subject. Upload, preview, download, or delete.'
              : 'Access educational materials and study resources.'}
          </p>
        </div>
        {user?.role === 'TUTOR' && (
          <button
            onClick={() => {
              setUploadData({
                title: '',
                description: '',
                subjectIds: [],
                file: null,
              });
              setShowUploadForm(true);
            }}
            className="btn btn-primary inline-flex items-center gap-2 self-start"
          >
            <Plus className="w-4 h-4" />
            <span>Upload Resource</span>
          </button>
        )}
      </div>

      {showUploadForm && user?.role === 'TUTOR' && (
        <div
          className="fixed inset-0 z-50 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setShowUploadForm(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Upload resource"
        >
          <div
            id="upload-form-card"
            className="card max-w-3xl w-full my-8"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="eyebrow">Upload</div>
              <h2 className="font-serif text-h3 text-ink mt-2">New resource</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowUploadForm(false)}
              className="p-2 text-muted hover:text-ink"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Title *</label>
                <input
                  type="text"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Subjects *</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-rule rounded-[2px] p-3 bg-paper">
                  {subjects.map((subject) => (
                    <label key={subject.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={uploadData.subjectIds.includes(subject.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setUploadData({
                              ...uploadData,
                              subjectIds: [...uploadData.subjectIds, subject.id],
                            });
                          } else {
                            setUploadData({
                              ...uploadData,
                              subjectIds: uploadData.subjectIds.filter((id) => id !== subject.id),
                            });
                          }
                        }}
                        className="rounded border-rule text-ochre focus:ring-ochre"
                      />
                      <span className="text-sm text-ink">{formatSubjectWithLevel(subject)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Description</label>
              <textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                rows={3}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">File *</label>
              <input
                type="file"
                onChange={(e) =>
                  setUploadData({ ...uploadData, file: e.target.files?.[0] || null })
                }
                className="input"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.html,.csv"
                required
              />
              <p className="text-xs text-muted mt-1">
                Supported formats: PDF, Word, Excel, PowerPoint, images (PNG/JPEG/WEBP), TXT, HTML, CSV. Max size: 20MB
              </p>
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="btn btn-primary">
                Upload Resource
              </button>
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {user?.role === 'STUDENT' ? (
        // -----------------------------------------------------------------
        // Student view — preserved layout, palette tokens only
        // -----------------------------------------------------------------
        <div className="space-y-6">
          {(() => {
            // Show every lesson that has resources attached — upcoming and past.
            // Resources stay available indefinitely.
            const filteredBookings = lessonBookings.filter(
              (booking) => booking.lessonResources.length > 0
            );

            if (filteredBookings.length === 0) {
              return (
                <div className="card">
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-muted mx-auto mb-4" />
                    <p className="text-ink">No lesson resources found.</p>
                    <p className="text-sm text-muted mt-1">
                      Resources will appear here when your tutor adds them to a lesson.
                    </p>
                  </div>
                </div>
              );
            }

            return filteredBookings.map((booking) => (
              <div key={booking.id} className="card">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-rule">
                  <div>
                    <h3 className="text-lg font-semibold text-ink font-serif">
                      {booking.subject.name} - {formatSubjectCategory(booking.subject.category)}
                    </h3>
                    <p className="text-sm text-muted">
                      {new Date(booking.startTime).toLocaleDateString()} with {booking.tutor.name}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium border ${
                      booking.status === 'COMPLETED'
                        ? 'bg-cream text-ochre border-ochre'
                        : 'bg-cream text-navy border-rule'
                    }`}
                  >
                    {booking.status}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {booking.lessonResources.map((lessonResource) => {
                    const ResourceIcon = FileText;
                    return (
                      <div
                        key={lessonResource.id}
                        className="border border-rule rounded-[2px] p-4 hover:border-ochre transition-colors bg-paper"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <ResourceIcon className="w-5 h-5 text-ochre" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-ink mb-1">
                                {lessonResource.resource.title}
                              </h4>
                              <div className="flex items-center space-x-2 text-xs text-muted">
                                <span className="bg-cream text-muted border border-rule px-2 py-1 rounded-full">
                                  {getFileTypeDisplayName(
                                    lessonResource.resource.mimeType,
                                    lessonResource.resource.fileExtension
                                  )}
                                </span>
                                {lessonResource.resource.fileSize && (
                                  <span>{formatFileSize(lessonResource.resource.fileSize)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {lessonResource.resource.description && (
                          <p className="text-sm text-muted mb-3 line-clamp-2">
                            {lessonResource.resource.description}
                          </p>
                        )}

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted">
                            Assigned: {new Date(lessonResource.assignedAt).toLocaleDateString()}
                          </span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDownload(lessonResource.resource.id)}
                              className="flex items-center text-sm text-ochre hover:underline transition-colors"
                              title="Download file"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      ) : user?.role === 'TUTOR' ? (
        // -----------------------------------------------------------------
        // Tutor view — bucket grid
        // -----------------------------------------------------------------
        <div className="space-y-6">
          <div className="card">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="eyebrow">Study Materials</div>
                <h2 className="font-serif text-h3 text-ink mt-2">
                  {Array.isArray(subjects)
                    ? subjects.find((s) => s.id === selectedSubject)?.name || 'All subjects'
                    : 'All subjects'}
                </h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted" />
                  <input
                    type="text"
                    placeholder="Search resources..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10 md:w-64"
                  />
                </div>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="input"
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {formatSubjectWithLevel(subject)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card">
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ochre mx-auto"></div>
                <p className="text-muted mt-2">Loading resources...</p>
              </div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="card">
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted mx-auto mb-4" />
                <p className="text-ink">No resources found.</p>
                <p className="text-sm text-muted mt-1">
                  Upload your first resource to get started.
                </p>
              </div>
            </div>
          ) : (
            renderTutorBucketGrid()
          )}
        </div>
      ) : (
        <div className="card">
          <div className="text-center py-8">
            <p className="text-muted">Access denied. Please contact your administrator.</p>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewResource && previewBlobUrl && (
        <div
          className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div
            className="bg-paper w-full max-w-5xl h-[85vh] rounded-card flex flex-col overflow-hidden border border-rule"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-rule">
              <div className="min-w-0">
                <div className="eyebrow">Preview</div>
                <h3 className="font-serif text-h3 text-ink truncate mt-1">
                  {previewResource.title}
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleDownload(previewResource.id)}
                  className="btn btn-secondary text-sm px-3 py-2"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
                <button
                  onClick={closePreview}
                  className="p-2 text-muted hover:text-ink"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-cream">
              <iframe
                src={previewBlobUrl}
                title={previewResource.title}
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
